<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div v-show="!areAllActionsDisabled">
    <div
      v-if="isPopupVisible"
      class="full-screen"
      @click="isPopupVisible = false" />
    <div
      v-if="actions.length > 0"
      class="entity-actions">
      <button
        v-tooltip="shouldShowLoader ? $t('messages.loadingOfSelectionsIsInProgress') : null"
        class="entity-actions-btn btn btn-primary-hollow"
        :disabled="shouldShowLoader"
        @click="entityActionsClicked">
        <loader-small v-if="shouldShowLoader" />
        <icon-action v-else />
        {{ $t('names.action', { n: 2 }) }}...
      </button>
      <div
        ref="actionsPopup"
        class="entity-actions-popup"
        :class="{ visible: isPopupVisible, 'pop-right': showOpenThePopupOnTheRight }">
        <ul>
          <li
            v-for="action in actions"
            :key="action.name"
            :class="getClassesForAction(action)"
            :data-test-name="action.name"
            @click="dispatchAction(action)">
            <component :is="action.icon" />
            <div
              v-sanitize-html="action.name"
              class="d-flex align-items-center" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { type Component, computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';

  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import IconAction from '@/common/icons/monochrome/Action.vue';

  export interface IAction {
    name: string;
    icon: Component;
    type: string;
    $isDisabled: boolean;
  }

  interface IProps {
    actions: IAction[];
    shouldShowLoader?: boolean;
  }

  const { actions } = defineProps<IProps>();

  interface IEmits {
    (event: 'changed', action: IAction): void;
    (event: 'clicked'): void;
  }

  const emits = defineEmits<IEmits>();

  const actionsPopup = useTemplateRef('actionsPopup');
  const isPopupVisible = ref(false);
  const showOpenThePopupOnTheRight = ref(false);

  const areAllActionsDisabled = computed(() => actions.filter((x) => x.$isDisabled).length === actions.length);

  onMounted(() => {
    window.addEventListener('resize', onWindowResized);
    onWindowResized();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onWindowResized);
  });

  function dispatchAction(action: IAction) {
    if (action.$isDisabled) return;

    isPopupVisible.value = false;
    emits('changed', action);
  }

  function entityActionsClicked() {
    isPopupVisible.value = !isPopupVisible.value;
    emits('clicked');
  }

  function onWindowResized() {
    if (!actionsPopup.value) {
      return;
    }
    showOpenThePopupOnTheRight.value =
      actionsPopup.value.getBoundingClientRect().x + actionsPopup.value.getBoundingClientRect().width + 500 <=
      window.innerWidth;
  }
  function getClassesForAction(action: IAction) {
    const classes = { disabled: !!action.$isDisabled };
    classes[action.type] = true;
    return classes;
  }
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .entity-actions {
    position: relative;
    margin-left: 1.2rem;

    .entity-actions-popup {
      transition: all 0.25s cubic-bezier(0.2, 1, 0.2, 1);
      transform: scaleY(0);
      transform-origin: top;
      opacity: 0;
      position: absolute;
      border-radius: 0.4rem;
      overflow: hidden;
      min-width: 25rem;
      box-shadow: $shadow;
      z-index: 5;
      right: 0;
      top: 3.7rem;
      font-size: 1.2rem;
      font-weight: 600;
      border: solid 0.1rem $borders;

      &.pop-right {
        right: auto;
        left: 0;
      }

      color: var(--primaryColor);

      &.visible {
        transition: all 0.25s cubic-bezier(0.2, 1, 0.2, 1);
        transform: scaleY(1);
        opacity: 1;
      }

      ul {
        padding: 0;
        margin: 0;

        li {
          color: var(--textColor);
          border-bottom: solid 0.1rem $borders-light;

          display: flex;
          align-items: center;
          padding: 1.2rem 1.4rem;
          background: $almost-white;
          cursor: pointer;

          &:nth-of-type(odd) {
            background: #f7f7f7;
          }

          &:hover {
            color: var(--primaryColor);

            background: #fff;

            svg {
              stroke: var(--primaryColor);
              fill: var(--primaryColor);
            }

            &:last-of-type {
              border-bottom: none;
            }
          }

          &:last-of-type {
            border-bottom: none;
          }

          svg {
            stroke: var(--textMutedColor);
            fill: var(--textMutedColor);

            width: 1.6rem;
            height: 1.6rem;
            margin-right: 1rem;
          }

          &.disabled {
            cursor: no-drop;
            background-color: #eeeeee;

            color: var(--textMutedColor);

            svg {
              stroke: var(--textMutedColor);
              fill: var(--textMutedColor);
            }
          }
        }
      }
    }
  }
</style>
